import {
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { type SessionRecord } from "@/shared/domain/sessions/SessionRecord";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseSessionParams {
  /**
   * Identifier of the Session to fetch. Required — the caller must
   * narrow ``sessionId`` to a string (e.g. by branching on the value
   * from ``useParams``) before invoking this hook.
   */
  sessionId: string;
}

/**
 * Fetches one Session record by id.
 *
 * Suspends until the response is available and throws to the nearest
 * ``<ErrorBoundary>`` on failure, so callers can keep the loaded view
 * free of ``isLoading`` / ``error`` branching.
 */
export function useSession({
  sessionId,
}: UseSessionParams): UseSuspenseQueryResult<SessionRecord, Error> {
  const { sessionService } = useDomainServices();
  return useSuspenseQuery<SessionRecord, Error>({
    queryKey: sessionQueryKeys.detail(sessionId),
    queryFn: ({ signal }) =>
      sessionService.getSessionById(sessionId, {
        abortSignal: signal,
      }),
  });
}
